import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { 
  LayoutDashboard, Home, Users, FileText, Calendar, DollarSign, 
  CreditCard, BarChart3, Wrench, Building2, Settings, LogOut, Menu, X, AlertTriangle, UserCog, Receipt
} from 'lucide-react';

const AdminLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const { t, language, setLanguage } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = React.useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const navItems = [
    { path: '/dashboard', icon: LayoutDashboard, label: t('dashboard'), roles: ['super_admin', 'company_admin', 'finance', 'operations', 'maintenance'] },
    { path: '/properties', icon: Building2, label: t('properties'), roles: ['super_admin', 'company_admin'] },
    { path: '/units', icon: Home, label: t('units'), roles: ['super_admin', 'company_admin', 'operations'] },
    { path: '/tenants', icon: Users, label: t('tenants'), roles: ['super_admin', 'company_admin', 'operations'] },
    { path: '/contracts', icon: FileText, label: t('contracts'), roles: ['super_admin', 'company_admin', 'operations'] },
    { path: '/reservations', icon: Calendar, label: t('reservations'), roles: ['super_admin', 'company_admin', 'operations'] },
    { path: '/accruals', icon: DollarSign, label: t('accruals'), roles: ['super_admin', 'company_admin', 'finance'] },
    { path: '/payments', icon: CreditCard, label: t('payments'), roles: ['super_admin', 'company_admin', 'finance'] },
    { path: '/reports', icon: BarChart3, label: t('reports'), roles: ['super_admin', 'company_admin', 'finance'] },
    { path: '/maintenance', icon: Wrench, label: t('maintenance'), roles: ['super_admin', 'company_admin', 'operations', 'maintenance'] },
    { path: '/debt-tracking', icon: AlertTriangle, label: t('debtTracking'), roles: ['super_admin', 'company_admin', 'finance'] },
    { path: '/dues-tracking', icon: Receipt, label: t('duesTracking'), roles: ['super_admin', 'company_admin', 'finance'] },
    { path: '/bank-accounts', icon: CreditCard, label: t('bankAccounts'), roles: ['super_admin', 'company_admin'] },
    { path: '/user-management', icon: UserCog, label: t('userManagement'), roles: ['super_admin', 'company_admin'] },
  ];

  const filteredNavItems = navItems.filter(item => item.roles.includes(user?.role));

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      {/* Mobile Header */}
      <div className="lg:hidden bg-white border-b border-black px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Building2 className="w-6 h-6" />
          <span className="font-heading text-lg font-bold">RentFlow</span>
        </div>
        <button onClick={() => setSidebarOpen(!sidebarOpen)} className="p-2">
          {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      <div className="flex">
        {/* Sidebar */}
        <aside className={`
          fixed lg:static inset-y-0 left-0 z-50
          w-64 bg-white border-r border-black
          transform transition-transform duration-200 ease-in-out
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          lg:min-h-screen flex flex-col
        `}>
          {/* Logo */}
          <div className="hidden lg:flex items-center gap-3 px-6 py-6 border-b border-gray-200 flex-shrink-0">
            <Building2 className="w-8 h-8" />
            <span className="font-heading text-xl font-bold tracking-tight">RentFlow</span>
          </div>

          {/* Navigation */}
          <nav className="p-4 space-y-1 flex-1 overflow-y-auto">
            {filteredNavItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className={`sidebar-link ${isActive ? 'active' : ''}`}
                  data-testid={`nav-${item.path.slice(1)}`}
                >
                  <Icon className="w-5 h-5" />
                  {item.label}
                </Link>
              );
            })}
          </nav>

          {/* Bottom Section */}
          <div className="p-4 border-t border-gray-200 flex-shrink-0">
            {/* Language Toggle */}
            <div className="flex border border-gray-300 mb-4">
              <button
                onClick={() => setLanguage('en')}
                className={`flex-1 py-2 text-sm font-medium transition-colors ${
                  language === 'en' ? 'bg-black text-white' : 'bg-white text-black hover:bg-gray-100'
                }`}
              >
                EN
              </button>
              <button
                onClick={() => setLanguage('tr')}
                className={`flex-1 py-2 text-sm font-medium transition-colors ${
                  language === 'tr' ? 'bg-black text-white' : 'bg-white text-black hover:bg-gray-100'
                }`}
              >
                TR
              </button>
            </div>

            {/* User Info */}
            <div 
              className="flex items-center gap-3 px-2 mb-3 cursor-pointer hover:bg-gray-100 p-2 transition-colors"
              onClick={() => { navigate('/profile'); setSidebarOpen(false); }}
              data-testid="nav-profile"
            >
              <div className="w-8 h-8 bg-black text-white flex items-center justify-center text-sm font-bold">
                {user?.name?.charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{user?.name}</p>
                <p className="text-xs text-gray-500 capitalize">{user?.role?.replace('_', ' ')}</p>
              </div>
            </div>

            {/* Logout */}
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50 transition-colors"
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5" />
              {t('logout')}
            </button>
          </div>
        </aside>

        {/* Overlay */}
        {sidebarOpen && (
          <div 
            className="fixed inset-0 bg-black/50 z-40 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Main Content */}
        <main className="flex-1 p-6 lg:p-8 min-h-screen">
          {children}
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
